// A team's banner, picked in Edit team (PLAN-2026-10-24-team-banners §4): a
// tile for each, drawn as it would look on the team's own colors, so
// choosing is looking rather than imagining, as the badges and the house
// colors are.
//
// IN THE CATALOG'S ORDER after None: *Follow the seasons* first, drawn as
// the scene it shows today; the everyday ones; a seasonal one on its days,
// with its tag, and the team's own whatever the day; and an earned one once
// earned, or else locked with what earns it, as the face maker's earned parts
// are (B-497). Locked only once the account's achievements are read: until
// then nothing tells somebody who made the playoffs that they have not.

import {
  TEAM_BANNERS,
  bannerOffered,
  bannerSceneOn,
  type BannerChoice,
  type BannerScene,
  type TeamBannerId,
  type TeamColors,
} from '@fsp/domain';
import { Icon } from '../../kit/Icon';
import { BannerArt } from '../../kit/TeamBanner';

export function BannerPicker({
  colors,
  value,
  kept,
  earned,
  onChange,
}: {
  colors: TeamColors;
  /** The banner chosen in the sheet; null is none. */
  value: TeamBannerId | null;
  /** The banner the team has now, offered whatever the day. */
  kept: TeamBannerId | null;
  /**
   * The achievements the account holds that earn a banner; null until they
   * are read, when an earned banner is neither offered nor shown locked.
   */
  earned: readonly string[] | null;
  onChange: (banner: TeamBannerId | null) => void;
}) {
  const today = new Date();
  const all = TEAM_BANNERS as readonly BannerChoice[];
  const shown = all.filter((b) =>
    bannerOffered(b, today, b.id === kept || b.id === value, earned ?? []),
  );
  const toEarn =
    earned === null ? [] : all.filter((b) => b.earnedBy !== undefined && !shown.includes(b));
  const tile = (id: TeamBannerId | null, name: string, scene: BannerScene | null, tag?: string) => (
    <button
      key={id ?? 'none'}
      type="button"
      className="fsp-banner-tile"
      aria-pressed={value === id}
      onClick={() => onChange(id)}
    >
      <span className="fsp-banner-tile-art fsp-has-banner">
        {scene !== null ? <BannerArt scene={scene} colors={colors} /> : null}
        {tag ? (
          <span className="fsp-banner-tile-tag" aria-hidden>
            {tag}
          </span>
        ) : null}
      </span>
      <span className="fsp-banner-tile-name">{name}</span>
    </button>
  );
  return (
    <div>
      <div className="fsp-field-label" id="fsp-banner-label">
        Banner
      </div>
      <div className="fsp-sub fsp-banner-hint">Behind your team on My Team and Game Day.</div>
      <div className="fsp-banner-tiles" role="group" aria-labelledby="fsp-banner-label">
        {tile(null, 'None', null)}
        {shown.map((b) =>
          tile(
            b.id as TeamBannerId,
            b.name,
            bannerSceneOn({ banner: b.id as TeamBannerId }, today),
            b.season?.tag ?? (b.earnedBy ? 'Earned' : undefined),
          ),
        )}
        {toEarn.map((b) => (
          <button
            key={b.id}
            type="button"
            className="fsp-banner-tile is-to-earn"
            aria-disabled
            aria-label={`${b.name}, not earned yet. Its achievement: ${b.earnedHow ?? ''}`}
            title={`${b.name}: its achievement, ${b.earnedHow ?? ''}`}
          >
            <span className="fsp-banner-tile-art fsp-has-banner">
              <BannerArt scene={b.id as BannerScene} colors={colors} />
              <span className="fsp-banner-tile-lock" aria-hidden>
                <Icon name="locked" size="xs" />
              </span>
            </span>
            <span className="fsp-banner-tile-name">{b.earnedHow}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
